import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
import { narrativeMediaTruthClassSchema } from "./narrative-visual-production.js";
import { opaqueIdSchema, sha256Schema } from "./opaque.js";
export const narrativeIntegrationCapabilitySchema = z
    .object({
    contract: z.literal("narrative-integration-capability/1"),
    product: narrativeBookProductSchema,
    lc95: z
        .object({
        status: z.enum(["AVAILABLE", "MISSING_PUBLISHED_SEAM"]),
        requiredInterface: z.literal("adapter registration, request/result envelope, fixture provider, retries, provenance, cost, and validation hooks"),
        integrationModule: z.string().trim().min(1).max(300).nullable(),
    })
        .strict(),
    activeTextMode: z.enum(["FIXTURE", "LC95"]),
    fixtureFallbackAvailable: z.literal(true),
    remoteDispatchEnabled: z.boolean(),
    paidDispatchEnabled: z.boolean(),
    blockers: z.array(z.string().trim().min(1).max(1_000)).max(20),
})
    .strict()
    .superRefine((value, context) => {
    if (value.lc95.status === "MISSING_PUBLISHED_SEAM" && value.activeTextMode === "LC95")
        context.addIssue({
            code: "custom",
            path: ["activeTextMode"],
            message: "LC-95 cannot be active until its published seam is present.",
        });
    if (value.paidDispatchEnabled && !value.remoteDispatchEnabled)
        context.addIssue({
            code: "custom",
            path: ["paidDispatchEnabled"],
            message: "Paid dispatch cannot be enabled while remote dispatch is disabled.",
        });
});
export const narrativeGenerationExecutionSchema = z
    .object({
    contract: z.literal("narrative-generation-execution/1"),
    product: narrativeBookProductSchema,
    mode: z.enum(["FIXTURE", "LOCAL_MODEL", "LC95"]),
    adapterId: z.string().trim().min(1).max(200),
    status: z.enum(["COMPLETED", "BLOCKED"]),
    prompt: z
        .object({
        id: z.string().trim().min(1).max(200),
        version: z.string().trim().min(1).max(100),
        checksumSha256: sha256Schema,
    })
        .strict(),
    inputs: z
        .object({
        sourceRevisionChecksums: z.array(sha256Schema).min(1).max(100),
        evidenceChecksumSha256: sha256Schema,
        privacyProjectionChecksumSha256: sha256Schema,
    })
        .strict(),
    outputChecksumSha256: sha256Schema.nullable(),
    providerCallCount: z.number().int().nonnegative(),
    paidProviderCallCount: z.number().int().nonnegative(),
    attempts: z.number().int().min(0).max(3),
    repairs: z.number().int().min(0).max(2),
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
        costMicros: z.number().int().nonnegative(),
    })
        .strict(),
    validation: z
        .object({
        privacy: z.enum(["PASS", "FAIL"]),
        provenance: z.enum(["PASS", "FAIL"]),
        chronology: z.enum(["PASS", "FAIL"]),
        attribution: z.enum(["PASS", "FAIL"]),
        repairBounds: z.enum(["PASS", "FAIL"]),
    })
        .strict(),
    blockers: z.array(z.string().trim().min(1).max(1_000)).max(20),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "COMPLETED") !== (value.outputChecksumSha256 !== null))
        context.addIssue({
            code: "custom",
            path: ["outputChecksumSha256"],
            message: "Only a completed execution may carry an output checksum.",
        });
    if (value.status === "COMPLETED" && Object.values(value.validation).includes("FAIL"))
        context.addIssue({
            code: "custom",
            path: ["validation"],
            message: "A completed generation execution must pass every validation gate.",
        });
    if (value.paidProviderCallCount > value.providerCallCount)
        context.addIssue({
            code: "custom",
            path: ["paidProviderCallCount"],
            message: "Paid calls cannot exceed total provider calls.",
        });
});
export const narrativeImageProviderRequestSchema = z
    .object({
    contract: z.literal("narrative-image-provider-request/1"),
    product: narrativeBookProductSchema,
    operationId: z.string().trim().min(1).max(200),
    assetId: z.string().trim().min(1).max(200),
    truthClass: narrativeMediaTruthClassSchema,
    route: z.enum(["BOUNDED_RESTORATION_EDIT", "GENERATE_EDITORIAL_ILLUSTRATION"]),
    modelId: z.string().regex(/^gpt-image-2(?:$|[.-])/u),
    purpose: z.literal("EDITORIAL_KEEPSAKE"),
    size: z
        .string()
        .regex(/^\d{3,4}x\d{3,4}$/u)
        .default("2048x2048"),
    quality: z.literal("medium").default("medium"),
    prompt: z.string().trim().min(40).max(4_000),
    promptChecksumSha256: sha256Schema,
    referenceSourceIds: z.array(z.string().trim().min(1).max(200)).max(4),
    referenceChecksumsSha256: z.array(sha256Schema).max(4),
    depictsIdentifiablePeople: z.boolean(),
    candidateLimit: z.number().int().min(1).max(2),
    repairLimit: z.number().int().min(0).max(1),
    authorization: z
        .object({
        evidenceAndPrivacyPassed: z.boolean(),
        rightsAndConsentPassed: z.boolean(),
        ownerRequestedOperation: z.boolean(),
        budgetReserved: z.boolean(),
        providerDispatchAllowed: z.boolean(),
        paidCallAllowed: z.boolean(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    const restoration = value.route === "BOUNDED_RESTORATION_EDIT";
    if (restoration && value.truthClass !== "DOCUMENTARY_RESTORATION")
        context.addIssue({
            code: "custom",
            path: ["truthClass"],
            message: "A restoration route requires DOCUMENTARY_RESTORATION truth class.",
        });
    if (restoration && value.referenceSourceIds.length === 0)
        context.addIssue({
            code: "custom",
            path: ["referenceSourceIds"],
            message: "A restoration requires at least one immutable owner reference.",
        });
    if (value.referenceSourceIds.length !== value.referenceChecksumsSha256.length)
        context.addIssue({
            code: "custom",
            path: ["referenceChecksumsSha256"],
            message: "Every ordered reference requires an exact checksum.",
        });
    if (value.depictsIdentifiablePeople)
        context.addIssue({
            code: "custom",
            path: ["depictsIdentifiablePeople"],
            message: "Narrative image operations cannot generate or alter an identifiable likeness.",
        });
});
export const narrativeImageProviderExecutionSchema = z
    .object({
    contract: z.literal("narrative-image-provider-execution/1"),
    product: narrativeBookProductSchema,
    operationId: z.string().trim().min(1).max(200),
    assetId: z.string().trim().min(1).max(200),
    status: z.enum(["BLOCKED", "COMPLETED", "REJECTED"]),
    providerCallCount: z.number().int().nonnegative().max(3),
    paidProviderCallCount: z.number().int().nonnegative().max(3),
    modelId: z.string().regex(/^gpt-image-2(?:$|[.-])/u),
    size: z
        .string()
        .regex(/^\d{3,4}x\d{3,4}$/u)
        .default("2048x2048"),
    quality: z.literal("medium").default("medium"),
    providerRequestId: z.string().trim().min(1).max(300).nullable().default(null),
    pricingVersion: z.string().trim().min(1).max(100).nullable().default(null),
    outputChecksumSha256: sha256Schema.nullable(),
    referenceChecksumsSha256: z.array(sha256Schema).max(4),
    usage: z
        .object({
        totalTokens: z.number().int().nonnegative(),
        costMicros: z.number().int().nonnegative(),
    })
        .strict(),
    ownerReviewRequired: z.literal(true),
    approvedForPlacement: z.literal(false),
    blockers: z.array(z.string().trim().min(1).max(1_000)).max(20),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "COMPLETED") !== (value.outputChecksumSha256 !== null))
        context.addIssue({
            code: "custom",
            path: ["outputChecksumSha256"],
            message: "Only a completed provider execution may carry an output checksum.",
        });
    if (value.paidProviderCallCount > value.providerCallCount)
        context.addIssue({
            code: "custom",
            path: ["paidProviderCallCount"],
            message: "Paid calls cannot exceed total provider calls.",
        });
});
export const narrativeApprovalSubjectSchema = z.enum([
    "SOURCE_CORRECTION",
    "PRIVACY_BOUNDARY",
    "CHAPTER_PLAN",
    "ATTRIBUTED_PASSAGE",
    "SHARED_WE_PASSAGE",
    "MEDIA_ASSET",
    "REPRESENTATIVE_PROOF",
    "FINAL_BOOK",
]);
export const narrativeApprovalLedgerSchema = z
    .object({
    contract: z.literal("narrative-approval-ledger/1"),
    product: narrativeBookProductSchema,
    bookId: opaqueIdSchema,
    editionRevisionId: z.string().trim().min(1).max(200),
    entries: z.array(z
        .object({
        approvalId: z.string().trim().min(1).max(200),
        subject: narrativeApprovalSubjectSchema,
        subjectId: z.string().trim().min(1).max(200),
        scope: z.enum(["INDIVIDUAL", "JOINT"]),
        requiredApproverPersonIds: z.array(opaqueIdSchema).min(1).max(10),
        decisions: z.array(z
            .object({
            personId: opaqueIdSchema,
            decision: z.enum(["PENDING", "APPROVED", "CHANGES_REQUESTED", "OMITTED"]),
            decidedAt: z.string().datetime().nullable(),
            revisionId: z.string().trim().min(1).max(200),
        })
            .strict()),
        status: z.enum(["PENDING", "APPROVED", "CHANGES_REQUESTED", "INVALIDATED"]),
        invalidatedAt: z.string().datetime().nullable(),
        invalidationReason: z.string().trim().min(1).max(1_000).nullable(),
    })
        .strict()),
})
    .strict()
    .superRefine((value, context) => {
    for (const [index, entry] of value.entries.entries()) {
        const required = new Set(entry.requiredApproverPersonIds);
        const decisions = new Map(entry.decisions.map((decision) => [decision.personId, decision]));
        if (entry.scope === "JOINT" && required.size < 2)
            context.addIssue({
                code: "custom",
                path: ["entries", index, "requiredApproverPersonIds"],
                message: "A joint approval requires at least two distinct approvers.",
            });
        if ([...required].some((personId) => !decisions.has(personId)))
            context.addIssue({
                code: "custom",
                path: ["entries", index, "decisions"],
                message: "Every required approver needs an explicit decision record.",
            });
        if (entry.status === "APPROVED" &&
            [...required].some((personId) => decisions.get(personId)?.decision !== "APPROVED"))
            context.addIssue({
                code: "custom",
                path: ["entries", index, "status"],
                message: "Approval requires every named approver to approve the same revision.",
            });
        if ((entry.status === "INVALIDATED") !== (entry.invalidatedAt !== null))
            context.addIssue({
                code: "custom",
                path: ["entries", index, "invalidatedAt"],
                message: "Only invalidated approvals may carry an invalidation timestamp.",
            });
    }
});
export const narrativeReleaseEvidenceSchema = z
    .object({
    contract: z.literal("narrative-release-evidence/1"),
    product: narrativeBookProductSchema,
    catalog: z
        .object({
        candidateFormat: z.enum(["trade-color-6x9", "trade-color-8.5x8.5"]),
        decision: z.enum(["PENDING", "APPROVED", "REJECTED"]),
        pricingApproved: z.boolean(),
        unitEconomicsApproved: z.boolean(),
    })
        .strict(),
    accessibility: z
        .object({
        taggedPdf: z.boolean(),
        readingOrderVerified: z.boolean(),
        screenReaderTested: z.boolean(),
        scriptCompleteFallbackBundled: z.boolean(),
    })
        .strict(),
    physicalProof: z
        .object({
        vendorPackageId: z.string().trim().min(1).max(200).nullable(),
        proofReceivedAt: z.string().datetime().nullable(),
        reviewerPersonId: opaqueIdSchema.nullable(),
        disposition: z.enum(["NOT_ORDERED", "PENDING", "APPROVED", "REJECTED"]),
    })
        .strict(),
    vendorSubmissionEnabled: z.literal(false),
    commerceActivationEnabled: z.literal(false),
    status: z.enum(["BLOCKED", "READY_FOR_SEPARATE_ACTIVATION_REVIEW"]),
    blockers: z.array(z.string().trim().min(1).max(1_000)).max(30),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "BLOCKED") !== value.blockers.length > 0)
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Release status must agree with its blocker list.",
        });
});
//# sourceMappingURL=narrative-workflow.js.map